import { NgIf, NgFor, AsyncPipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, Inject, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardTitle, MatCardTitleGroup, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatLabel, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartActions, CartLine, OrderTypes, ValidationMessage, ValidationMessageSeverityLevel, ValidationMessageSource } from '@next/core-lib/cart';
import { AbstractDialogData, AbstractDialogResultData, DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlags, FeatureFlagService } from '@next/core-lib/featureflag';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';

import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { RouterSelectors } from '@next/core-lib/router-store';
import { TableConfig, AgrTableModule } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { addMinutes, differenceInMilliseconds, format, isAfter, parseISO } from 'date-fns';
import { combineLatest, ConnectableObservable, Observable, race, ReplaySubject, Subject, Subscription, switchMap, timer } from 'rxjs';
import { debounceTime, filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { Environment } from 'src/environments/environment.model';

import { createColumnComponents, setColumnValues, setTableValues } from './cart-helpers/table-setup';
import {
  CartFlexibilityDateViewModel,
  ChooseFlexibilityDateDialogComponent,
} from '../../../components/choose-flexibility-date-dialog/choose-flexibility-date-dialog.component';
import { AppState, CartColumns, ValidationFeedback } from '../../models/app.models';
import * as AppActions from '../../store/actions';
import { selectCartValidationMessages, selectNotReadyForSimulation } from '../../store/selectors';
import { ColumnBlockedForReturnComponent } from './components/column-blocked-for-return/column-blocked-for-return.component';
import { ColumnDeliveryDateComponent } from './components/column-delivery-date/column-delivery-date.component';
import { ColumnDeliveryPeriodComponent } from './components/column-delivery-period/column-delivery-period.component';
import { ColumnExternalInvoiceIdForReturnComponent } from './components/column-external-invoice-id-for-return/column-external-invoice-id-for-return.component'; // eslint-disable-line max-len
import { ColumnPriceResultsComponent } from './components/column-price-results/column-price-results.component';
import { ColumnProductSupplementsComponent } from './components/column-product-supplements/column-product-supplements.component';
import { ColumnQuantityUnitInformationComponent } from './components/column-quantity-unit-information/column-quantity-unit-information.component'; // eslint-disable-line max-len
import { ColumnRemarksComponent } from './components/column-remarks/column-remarks.component';
import { ColumnSiloDistributionComponent } from './components/column-silo-distribution/column-silo-distribution.component';
import { ColumnSkuTitleComponent } from './components/column-sku-title/column-sku-title.component';
import { LabelDeliveryDateComponent } from './components/label-delivery-date/label-delivery-date.component';
import { OneDayDeliveryHelpDialogComponent } from './components/one-day-delivery-help-dialog/one-day-delivery-help-dialog.component';
import { OrderTypeHelpDialogComponent } from './components/ordertype-help-dialog/ordertype-help-dialog.component';
import { OrderTypeSwitchDialogComponent } from './components/ordertype-switch-dialog/ordertype-switch-dialog.component';
import { SubrowNotificationsComponent } from './components/subrow-notifications/subrow-notifications.component';

import { CustomerBlockedNotificationComponent } from '../../../standalone/customer-blocked-notification/customer-blocked-notification';
import { SearchComponent } from '../../../standalone/search/search.component';

import { FormatValidationMessagePipe } from '../../shared/pipes/format-validation-message.pipe';
import { PriceNotificationComponent } from '../cart-confirmation/price-notification/price-notification.component';

interface ViewModel {
  isAtLeastAdvisorOrSalesOrder: boolean;
  notReadyForSimulation: boolean;
  validationMessages: ValidationFeedback[];
  returnUrl: string;
  location: SelectedLocation;
}

interface CartLineWithFullWidthToggle extends CartLine {
  hasRemarksOrValidationErrors: boolean;
}

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatCard,
    MatCardTitle,
    MatIcon,
    MatCardTitleGroup,
    CustomerBlockedNotificationComponent,
    SearchComponent,
    MatButton,
    MatLabel,
    MatFormField,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatOption,
    PriceNotificationComponent,
    MatCardContent,
    AgrTableModule,
    MatCardActions,
    AsyncPipe,
    LowerCasePipe,
    AgrI18nSharedModule,
    FormatValidationMessagePipe,
  ],
})
export class CartComponent implements OnInit, OnDestroy {
  @Input() editable: boolean;
  @Input() showPrice: boolean;
  @Input() extendAccordionMobile: boolean;
  @Input() cartScreenType: string;
  columnSkuTitleRef: ComponentRef<ColumnSkuTitleComponent>;
  columnQuantityAndUnitInformationRef: ComponentRef<ColumnQuantityUnitInformationComponent>;
  columnSiloDistributionRef: ComponentRef<ColumnSiloDistributionComponent>;
  columnProductSupplementsRef: ComponentRef<ColumnProductSupplementsComponent>;
  columnDeliveryDateRef: ComponentRef<ColumnDeliveryDateComponent>;
  labelDeliveryDateRef: ComponentRef<LabelDeliveryDateComponent>;
  columnDeliveryPeriodRef: ComponentRef<ColumnDeliveryPeriodComponent>;
  columnExternalInvoiceIdForReturnRef: ComponentRef<ColumnExternalInvoiceIdForReturnComponent>;
  columnPriceResultsRef: ComponentRef<ColumnPriceResultsComponent>;
  cartLineSubrowRef: ComponentRef<SubrowNotificationsComponent>;
  columnRemarksRef: ComponentRef<ColumnRemarksComponent>;
  columnBlockedForReturnRef: ComponentRef<ColumnBlockedForReturnComponent>;
  orderTypes = OrderTypes;
  orderTypeControl = new FormControl();
  orderTypeOptions$: Observable<
    {
      value: OrderTypes;
      viewValue: string;
    }[]
  >;

  orderQuantities = new FormArray([]);
  orderQuantitiesIsPending = false;

  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([]),
    columns: null,
    selectColumn: false,
    pagination: false,
    actionColumn: false,
    hideActionLabel: true,
    primaryColumn: CartColumns.skuTitle,
    fullWidthRowClass: 'cart__remarks-and-validations-wrapper',
    fullWidthRowToggleColumn: CartColumns.hasRemarksOrValidationErrors,
  };

  vm$: Observable<ViewModel>;

  readonly isMobileDisplay$ = this.breakpointService.xs$;
  readonly isTabOrSmallerDisplay$ = this.breakpointService.ltMd$;

  topLevelClosingTimeWarning: ValidationMessage;
  debounceQuantityChange$ = new Subject<{ row: CartLine; control: AbstractControl }>();

  protected readonly isUserAtLeastAdvisor$ = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);

  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  private readonly notReadyForSimulation$ = this.store.select(selectNotReadyForSimulation(this.cart?.id));
  private validationMessages$: Observable<ValidationFeedback[]>;
  private readonly isAtLeastAdvisorOrSalesOrder$: Observable<boolean> = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole).pipe(
    take(1),
    map((isAtLeastAdvisor) => this.cart?.type !== OrderTypes.preSalesOrder || isAtLeastAdvisor),
  );
  private readonly returnUrl$: Observable<string | null> = this.store.select(RouterSelectors.getQueryParam('returnUrl')).pipe(
    take(1),
    map((queryParam, _) => {
      if (this.cart?.type === OrderTypes.preSalesOrder) {
        return this.cart?.metadata?.returnUrl ?? queryParam;
      }

      return null;
    }),
  );

  private readonly hasCustomerRole$ = this.store.select(AuthorizationSelectors.hasCustomerRole);
  private readonly customer$ = this.store.select(UserSelectors.selectCustomer);
  private readonly featureFlags$: Observable<string[]> = this.customer$.pipe(
    map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
    switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
  );

  private readonly hasAtLeastAdvisorRole$ = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);
  private readonly timerSubscriptions: Subscription[] = [];
  private readonly _cart$: ReplaySubject<Cart> = new ReplaySubject<Cart>(1);
  private _cart: Cart;

  private readonly _hasOneOrMoreCartLines$ = this._cart$.pipe(map((x) => x.lines.length >= 1));

  private readonly isValidCartTypeForOneDayDelivery = this._cart$.pipe(
    map((cart) => cart?.type === OrderTypes.salesOrder || cart?.type === OrderTypes.pickUpOrder),
  );

  readonly isOneDeliveryDateVisible$ = combineLatest([this.featureFlags$, this.isUserAtLeastAdvisor$, this.isValidCartTypeForOneDayDelivery]).pipe(
    map(
      ([featureFlags, isAtLeastAdvisor, isValidCartTypeForOneDayDelivery]) =>
        featureFlags.includes(FeatureFlags.enableOneDeliveryDate) && isAtLeastAdvisor && isValidCartTypeForOneDayDelivery,
    ),
  );

  readonly isOneDeliveryDateEnabled$ = combineLatest([this.isOneDeliveryDateVisible$, this._hasOneOrMoreCartLines$]).pipe(
    map((conditions) => conditions.every((condition) => condition)),
  );

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store<AppState>,
    public readonly locale: LocaleService,
    private readonly dialogService: DialogService,
    private readonly actionsListener$: ActionsSubject,
    private readonly featureFlagService: FeatureFlagService,
    container: ViewContainerRef,
    @Inject(EnvironmentInjector) public environment: Environment,
    @Inject(BreakpointServiceInjector) readonly breakpointService: BreakpointService,
  ) {
    createColumnComponents(this, container);
  }

  get cart() {
    return this._cart;
  }

  @Input() set cart(value: Cart) {
    this.setClosingTimeWarnings(value);

    this.tableConfig = { ...this.tableConfig, dataSource: new MatTableDataSource(value?.lines ?? []) };
    this._cart = value;
    this._cart$.next(this._cart);

    this.updateTableConfig();
  }

  ngOnInit() {
    this.validationMessages$ = this.store.select(selectCartValidationMessages(this.cart?.id, this.locale));

    this.orderTypeOptions$ = combineLatest([this.locale.locale$, this.getAvailableOrderTypes()]).pipe(
      map(([, availableOrderTypes]) => this.getAvailableOrderTypeOptions(availableOrderTypes)),
    );

    this.vm$ = combineLatest([
      this.isAtLeastAdvisorOrSalesOrder$.pipe(startWith(false)),
      this.notReadyForSimulation$.pipe(startWith(false)),
      this.validationMessages$.pipe(startWith<ValidationFeedback[]>([])),
      this.returnUrl$.pipe(startWith<string>(null)),
      this.location$.pipe(startWith<SelectedLocation>(null)),
    ]).pipe(
      map(([isAtLeastAdvisorOrSalesOrder, notReadyForSimulation, validationMessages, returnUrl, location]) => ({
        isAtLeastAdvisorOrSalesOrder,
        notReadyForSimulation,
        validationMessages,
        returnUrl,
        location,
      })),
    );

    this.debounceQuantityChange$
      .pipe(
        filter(({ row, control }) => row.quantity !== (control.value as number)),
        tap(() => {
          this.orderQuantitiesIsPending = true;
        }),
        debounceTime(1000),
        tap(({ row, control }) => {
          const selectedValue = control.value as number;
          if (selectedValue) {
            this.store.dispatch(
              CartActions.updateCartLineQuantity({
                cartId: this.cart?.id,
                cartLineId: row.id,
                quantity: selectedValue,
              }),
            );
          }

          this.orderQuantitiesIsPending = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.orderTypeControl.setValue(this.cart?.type);

    if (!this.editable || this.cart?.type === OrderTypes.returnableOrder) {
      this.orderTypeControl.disable();
    }

    this.orderTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .pipe(
        map((targetType: OrderTypes) => ({
          linesToBeRemoved: this.cart.lines.filter((line) => !line.orderingInformation.allowedOrderTypes?.includes(targetType)),
          targetType,
        })),
      )
      .subscribe(({ linesToBeRemoved, targetType }) => {
        if (linesToBeRemoved.length === 0) {
          this.changeCartType(targetType);
        } else {
          const cartEligibleForChange = linesToBeRemoved.length < this.cart.lines.length;
          const dialogStatus$ = this.openSwitchModal(this.cart.type, targetType, cartEligibleForChange, linesToBeRemoved);
          dialogStatus$.pipe(take(1), takeUntil(this.destroy$)).subscribe(({ status }) => {
            if (cartEligibleForChange && status === DIALOG_RETURN_TYPES.confirm) {
              this.changeCartType(targetType, linesToBeRemoved);
            } else {
              this.orderTypeControl.setValue(this.cart.type, { emitEvent: false });
            }
          });
          dialogStatus$.connect();
        }
      });

    this.updateTableConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateTableConfig = (): void => {
    if (this.featureFlags$ === undefined) {
      return;
    }

    combineLatest([
      this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole),
      this.store.select(AuthorizationSelectors.hasAtLeastCustomerRoleForApplicationKey('RemarksFeature')),
      this.featureFlags$,
      this.isTabOrSmallerDisplay$,
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([hasAtLeastAdvisorRole, hasRemarksFeature, featureFlags, isTabOrSmallerDisplay]) => {
        setColumnValues(this);
        setTableValues(this, hasAtLeastAdvisorRole, featureFlags, isTabOrSmallerDisplay, hasRemarksFeature);
      });
  };

  openOneDayDeliveryHelpModal(): void {
    this.dialogService.responsiveDialog(OneDayDeliveryHelpDialogComponent, {
      title: this.locale.translate('cart.one-day-delivery.info-title'),
      cancelButton: false,
      disableClose: true,
      confirmButtonText: this.locale.translate('cart.cart-help-modal.close'),
      confirmButton: true,
      cancelButtonText: this.locale.translate('cancel'),
    });
  }

  sendOneDayDeliveryRequest(): void {
    this.store.dispatch(
      CartActions.patchCartOneDayDeliveryDate({
        cartId: this.cart?.id,
      }),
    );
  }

  openOrderTypeHelpModal(): void {
    this.dialogService.responsiveDialog(OrderTypeHelpDialogComponent, {
      title: this.locale.translate('cart.cart-help-modal.title'),
      cancelButton: false,
      disableClose: true,
      confirmButtonText: this.locale.translate('cart.cart-help-modal.close'),
      confirmButton: true,
      cancelButtonText: this.locale.translate('cancel'),
    });
  }

  openSwitchModal(
    changeFrom: OrderTypes,
    changeTo: OrderTypes,
    cartEligibleForChange: boolean,
    cartlinesToRemove: CartLine[],
  ): ConnectableObservable<AbstractDialogResultData<AbstractDialogData>> {
    return this.dialogService.responsiveDialog(
      OrderTypeSwitchDialogComponent,
      {
        title: this.locale.translate('cart.cart-modal.title', {
          from: this.locale.translate(`cart.cart-order-type.${changeFrom}`),
          to: this.locale.translate(`cart.cart-order-type.${changeTo}`),
        }),
        cancelButtonText: this.locale.translate('cart.cart-modal.back'),
        confirmButtonText: this.locale.translate('cart.cart-modal.confirm'),
        confirmButton: true,
        changeFrom,
        changeTo,
        cartEligibleForChange,
        cartlinesToRemove,
      },
      { width: '50%' },
    );
  }

  changeCartType(type: OrderTypes, cartlinesToRemove?: CartLine[]) {
    this.store.dispatch(
      CartActions.changeCartType({
        cartId: this.cart?.id,
        cartType: type,
        lineIdsToDelete: cartlinesToRemove?.map((line) => line.id),
      }),
    );

    // Reset the contol value if something went wrong while updating the cart type.
    race(
      [CartActions.postCartSuccess, NotificationActions.errorSnackbar].map((action) =>
        this.actionsListener$.pipe(ofType(action), take(1), takeUntil(this.destroy$)),
      ),
    ).subscribe((action) => {
      if (action.type === NotificationActions.errorSnackbar.type) {
        this.orderTypeControl.setValue(this.cart.type, { emitEvent: false });
      }
    });
  }

  confirm(): void {
    this.store.dispatch(
      CartActions.patchSimulateCartRequest({
        cartId: this.cart?.id,
        profileId: this.cart?.profileId,
      }),
    );
  }

  delete(): void {
    this.returnUrl$
      .pipe(
        take(1),
        map((url?: string) => {
          this.store.dispatch(
            AppActions.deleteCartConfirmation({
              cartId: this.cart?.id,
              returnUrl: url,
              returnApplicationNameKey: this.cart?.metadata?.returnApplicationNameKey,
            }),
          );
        }),
      )
      .subscribe();
  }

  deleteLine(cartLineId: string): void {
    this.store.dispatch(
      AppActions.deleteLineFromCartConfirmation({
        cartId: this.cart?.id,
        cartLineId,
      }),
    );
  }

  getQuantityStep(row: CartLine): number {
    return row?.orderingInformation?.increaseOrderUnitQuantity ?? 1;
  }

  private removeClosingTimeWarnings(cart: Cart) {
    this.topLevelClosingTimeWarning = undefined;

    while (this.timerSubscriptions.length > 0) {
      this.timerSubscriptions.pop().unsubscribe();
    }

    cart?.lines?.forEach((line) => {
      line.validationMessages = line.validationMessages?.filter((message) => message.errorCode !== 'CVF-1002' && message.errorCode !== 'CVF-1001');
    });
  }

  private setClosingTimeWarnings(cart: Cart) {
    this.removeClosingTimeWarnings(cart);

    const closingTimes = cart?.lines.filter((line) => line?.deliveryInformation?.showClosingTime).map((line) => line?.deliveryInformation?.closingTimeUtc);

    if (!closingTimes || closingTimes.length === 0 || closingTimes.some((c) => !c)) {
      return;
    }

    const uniqueClosingTimes = Array.from(new Set(closingTimes));

    // Checks if all products have the same closing time
    if (uniqueClosingTimes.length === 1) {
      const firstCartLineWithClosingTime = cart?.lines.find(
        (line) => line.deliveryInformation?.showClosingTime === true && !!line.deliveryInformation?.closingTimeUtc,
      );
      const warning = this.getWarning(firstCartLineWithClosingTime);
      if (warning) {
        this.topLevelClosingTimeWarning = warning;
      }
    } else {
      cart.lines = cart.lines?.map((line: CartLineWithFullWidthToggle) => {
        const warning = this.getWarning(line);
        return warning
          ? {
              ...line,
              validationMessages: [warning, ...line.validationMessages],
              hasRemarksOrValidationErrors: true,
            }
          : line;
      });
    }

    uniqueClosingTimes.forEach((closingTime) => {
      const timeUntilClosingTimeWarningShouldShow = differenceInMilliseconds(parseISO(closingTime), addMinutes(new Date(), 30));

      // This sets a timer to trigger closingtime messages to update at 30min before closingtime (to show the warning message).
      if (timeUntilClosingTimeWarningShouldShow > 0) {
        const sub = timer(timeUntilClosingTimeWarningShouldShow)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.changeDetectorRef.detectChanges();
          });

        this.timerSubscriptions.push(sub);
      }
    });
  }

  private getWarning(cartLine: CartLine): ValidationMessage | false {
    const deliveryInformation = cartLine.deliveryInformation;

    if (!deliveryInformation) {
      return false;
    }

    const localNow = new Date();
    const closingTimeLocal = parseISO(deliveryInformation.closingTimeUtc);

    if (isAfter(localNow, closingTimeLocal)) {
      return {
        errorCode: 'CVF-1001',
        context: {},
        severityLevel: ValidationMessageSeverityLevel.Warning,
        source: ValidationMessageSource.site,
      };
    } else if (deliveryInformation.showClosingTime && isAfter(addMinutes(localNow, 30), closingTimeLocal)) {
      return {
        errorCode: 'CVF-1002',
        context: { closingTime: format(closingTimeLocal, 'HH:mm') },
        severityLevel: ValidationMessageSeverityLevel.Warning,
        source: ValidationMessageSource.site,
      };
    }
  }

  private getAvailableOrderTypes(): Observable<OrderTypes[]> {
    return combineLatest([this.hasCustomerRole$, this.featureFlags$, this.hasAtLeastAdvisorRole$]).pipe(
      map(([hasCustomerRole, featureFlags, hasAtLeastAdvisorRole]) => {
        let availableOrders: OrderTypes[];
        if (hasCustomerRole) {
          availableOrders = [OrderTypes.salesOrder];
        } else {
          availableOrders = Object.values(this.orderTypes).filter(
            (type) => ![OrderTypes.preSalesOrder, OrderTypes.trustOrder, OrderTypes.returnableOrder].includes(type),
          );
        }

        // Pickup is enabled only for PL
        if (!featureFlags.includes(FeatureFlags.enablePickupOrderType)) {
          availableOrders = availableOrders.filter((ordertype) => ordertype !== OrderTypes.pickUpOrder);
        }

        //GrainPreSales is enabled only for NL
        if (!featureFlags.includes(FeatureFlags.enableGrainPreSalesOrderType)) {
          availableOrders = availableOrders.filter((ordertype) => ordertype !== OrderTypes.grainPreSalesOrder);
        }

        // Today for Today order is not available for PL
        if (hasAtLeastAdvisorRole && !featureFlags.includes(FeatureFlags.enableTodayForTodayOrderType)) {
          availableOrders = availableOrders.filter((ordertype) => ordertype !== OrderTypes.rushOrder);
        }

        return availableOrders;
      }),
    );
  }

  private getAvailableOrderTypeOptions(availableOrderTypes: OrderTypes[]): { value: OrderTypes; viewValue: string }[] {
    if (this.cart?.type === OrderTypes.returnableOrder) {
      return [
        {
          value: OrderTypes.returnableOrder,
          viewValue: this.locale.translate(`cart.cart-order-type.${OrderTypes.returnableOrder}`),
        },
      ];
    }

    return availableOrderTypes.map((type) => ({
      value: type,
      viewValue: this.locale.translate(`cart.cart-order-type.${type}`),
    }));
  }

  openCartDatePicker() {
    const subscription = this.dialogService
      .fullscreenDialog(ChooseFlexibilityDateDialogComponent, {
        cartFlexibilityDate: this.cart as CartFlexibilityDateViewModel,
        deliveryInformation: this.cart.lines[0].deliveryInformation,
      })
      .subscribe(({ status, result }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          console.log('result', result);
        }

        subscription.unsubscribe();
      });
  }
}
